// Lebensbalken ueber zaehen Zielen — **reine Darstellung**.
//
// Der Laser feuert selbsttaetig und kennt keine Zielauswahl: der Spieler sieht
// heute, *dass* etwas beschossen wird, aber nicht, *wie lange noch*. Bei einem
// Waechter oder einem Jaeger — wirksam Zehntausende Trefferpunkte, weil die
// rohe Huelle mit der Zonenhaerte multipliziert wird — ist das der Unterschied
// zwischen „gleich faellt er" und „hier verliere ich nur Huelle". Der Kolossbrocken
// beantwortet die Frage laengst mit seinem Fortschrittsbogen; dies ist dasselbe
// fuer die normalen Ziele im Feld.
//
// Nichts hier beruehrt den Zustand. Der Zeitpunkt des letzten Treffers entsteht
// im mutierenden Pfad (`World.applyDamage`, `HostileField.damage` und ihre
// Server-Gegenstuecke) und wird hier nur **gelesen** (Gate FG-17).

import type Phaser from 'phaser';

import { clamp } from '../core/math';
import { enemy, rockKind, zonesFor } from '../data/balancing';

/**
 * Wie lange der Balken nach dem letzten Treffer stehen bleibt.
 *
 * Ohne das Ausblenden truege ein einmal angeschlagener und dann verlassener
 * Brocken seinen Balken, bis er nachwaechst — das waere Lauf statt Auskunft.
 */
export const BAR_LINGER = 3;

/**
 * Ab wie vielen Trefferpunkten der **Klasse** ein Ziel einen Balken bekommt.
 *
 * Die Schwelle haengt an der Klasse, nicht an `maxHp`: `maxHp` ist zonenskaliert,
 * ein Kiesel weit draussen haette sonst mehr Trefferpunkte als ein riesiger
 * Brocken vor der Haustuer, und die Auswahl waere je nach Aufenthaltsort eine
 * andere. Die Klassenwerte stehen zonenunabhaengig in der Tabelle.
 *
 * `medium` (9) und aufwaerts sowie der glaenzende Brocken (60) tragen damit
 * einen Balken, Kiesel (1) und kleiner Brocken (3) nicht — sie fallen im ersten
 * Schuss, und ein Balken dafuer waere ein Blinken ohne Aussage.
 */
export const ROCK_BAR_MIN_HP = 9;

/**
 * Dasselbe fuer Gegner: alles ab dem Splitterschwarm (176), nicht die
 * Schrottdrohne (6).
 *
 * Gemessen an der **wirksamen** Huelle — `hp` der Art mal Haerte ihrer Zone —
 * und nicht am rohen Tabellenwert. Der rohe Wert ist nur die halbe Zahl: das
 * Truemmergeschuetz von Akt II traegt eine 4 und steht in einer Zone mit
 * Haerte 900. Am rohen Wert gemessen bekaeme ausgerechnet der zaeheste
 * Anfangsgegner keinen Balken.
 */
export const ENEMY_BAR_MIN_HP = 20;

// Masse und Farben. Das sind **Darstellungsgrenzen, keine Balancing-Knoepfe**:
// sie formen keine Entscheidung des Spielers, sondern nur ein Bild — dieselbe
// Begruendung wie bei den Deckeln in `effects.ts`. Gezeichnet wird in
// Weltkoordinaten, also skaliert die Kamera sie mit; die Zahlen gelten fuer
// Zoomstufe 1.
const BAR_HEIGHT = 3;
const BAR_MIN_WIDTH = 14;
const BAR_MAX_WIDTH = 48;
/**
 * Breite je Radius. Deutlich **unter** dem vollen Durchmesser: `r` ist der
 * Hüllradius, das Vieleck darin fuellt ihn nie aus — ein Balken ueber `2r`
 * steht sichtbar breiter da als der Brocken, den er meint.
 */
const BAR_WIDTH_PER_R = 1.6;
/** Abstand ueber dem Rand des Objekts. */
const BAR_GAP = 5;

const BAR_BACK = 0x0e1218;
/** Warm heisst „da geht noch etwas", rot heisst „gleich faellt es". */
const BAR_HIGH = 0x59f07a;
const BAR_MID = 0xf0c437;
const BAR_LOW = 0xff6a5c;

/** Traegt ein Brocken dieser Klasse einen Balken? */
export function rockHasBar(kindId: string): boolean {
  const kind = rockKind(kindId);
  return kind !== undefined && kind.hp >= ROCK_BAR_MIN_HP;
}

/** Traegt ein Gegner dieser Art einen Balken? */
export function hostileHasBar(kindId: string): boolean {
  const kind = enemy(kindId);
  if (kind === undefined) return false;
  const zone = zonesFor(kind.minEra)[kind.minZone];
  return kind.hp * (zone ? zone.hp : 1) >= ENEMY_BAR_MIN_HP;
}

/**
 * Deckkraft des Balkens: voll, solange geschossen wird, danach ausblendend.
 *
 * `hitAge` ist die Zeit seit dem letzten Treffer — `Infinity` bei einem
 * unberuehrten Ziel, das damit sauber bei 0 landet.
 */
export function barAlpha(hitAge: number): number {
  if (!(hitAge < BAR_LINGER)) return 0;
  return clamp((BAR_LINGER - hitAge) / 0.9, 0, 1);
}

export function barColor(share: number): number {
  if (share > 0.5) return BAR_HIGH;
  if (share > 0.22) return BAR_MID;
  return BAR_LOW;
}

/**
 * Einen Balken ueber ein Objekt zeichnen.
 *
 * @param share Anteil der verbliebenen Trefferpunkte, 0…1
 * @param alpha Deckkraft aus `barAlpha`
 */
export function drawHealthBar(
  g: Phaser.GameObjects.Graphics,
  x: number, y: number, radius: number, share: number, alpha: number,
): void {
  const width = clamp(radius * BAR_WIDTH_PER_R, BAR_MIN_WIDTH, BAR_MAX_WIDTH);
  const left = x - width / 2;
  const top = y - radius - BAR_GAP - BAR_HEIGHT;
  const filled = clamp(share, 0, 1);

  g.fillStyle(BAR_BACK, 0.8 * alpha);
  g.fillRect(left - 1, top - 1, width + 2, BAR_HEIGHT + 2);
  g.fillStyle(barColor(filled), alpha);
  g.fillRect(left, top, width * filled, BAR_HEIGHT);
}
